/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { AppConfig } from '@/types/config';

type MarketingCloudConfig = AppConfig['marketingCloud'];

/**
 * Returns a browser-safe Marketing Cloud collect.js URL, or undefined when
 * unconfigured. Empty and non-HTTPS values are rejected so the pixel stays
 * aligned with strict CSP.
 */
export function getMarketingCloudCollectScriptUrl(config: MarketingCloudConfig): string | undefined {
    const scriptUrl = config?.scriptUrl?.trim();
    if (!scriptUrl) return undefined;

    try {
        const url = new URL(scriptUrl);
        return url.protocol === 'https:' ? url.toString() : undefined;
    } catch {
        return undefined;
    }
}
